
import { config } from "@/config/environment";
import { auth } from "@/lib/firebase";
import { User } from "firebase/auth";

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

// Get the current authenticated user from Firebase
const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

const getAuthHeaders = async () => {
  const user = getCurrentUser();
  const token = user ? await user.getIdToken() : '';
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '1',
  };
};

export async function createConnection(name: string, ageRating: string = "12+"): Promise<ApiResponse<{ id: string }>> {
  try {
    console.log("Creating connection with name:", name, "and age rating:", ageRating);
    const headers = await getAuthHeaders();
    const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.createSession}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name, ageRating }),
    });

    const data = await response.json();
    console.log("Create connection response:", data);

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create connection');
    }

    // Transform the API response (sessionId) to match our internal format (id)
    return { 
      success: true, 
      data: { 
        id: data.sessionId || data.id // Support both formats in case API response varies
      } 
    };
  } catch (error) {
    console.error('Error creating connection:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

export async function updateConnection(id: string, data: { name?: string; ageRating?: string }): Promise<ApiResponse<void>> {
  try {
    console.log("Updating connection with ID:", id, "Data:", data);
    const headers = await getAuthHeaders();
    const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.updateSession(id)}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update connection');
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating connection:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

export async function reconnectConnection(id: string): Promise<ApiResponse<{ id: string }>> {
  try {
    console.log("Reconnecting to connection with ID:", id);
    const headers = await getAuthHeaders();
    const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.reconnectSession(id)}`, {
      method: 'POST',
      headers,
    });

    const data = await response.json();
    console.log("Reconnect connection response:", data);

    if (!response.ok) {
      throw new Error(data.error || 'Failed to reconnect to session');
    }

    return { 
      success: true, 
      data: { 
        id: data.sessionId || id 
      } 
    };
  } catch (error) {
    console.error('Error reconnecting to session:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

export async function deleteConnection(id: string): Promise<ApiResponse<void>> {
  try {
    console.log("Deleting connection with ID:", id);
    const headers = await getAuthHeaders();
    const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.deleteSession(id)}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete connection');
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting connection:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.health}`, {
      headers: {
        'ngrok-skip-browser-warning': '1'
      }
    });
    return response.ok;
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
}
